// Zahlen-, Geld- und Datumsformate der Verwaltung (Produktfamilie R6):
// „13.091,69 €“, echtes Minus „−3,20 €“ (U+2212), geschütztes Leerzeichen (U+00A0) vor € und %,
// Geld immer mit zwei Nachkommastellen, „08.10.2026, 11:42“, kurz „08.10., 11:42“, „12:04 Uhr“, „+4,2 %“.
// Fehlende oder ungültige Werte ergeben „–“ (Gedankenstrich), damit Tabellen und Kacheln nie „NaN“ zeigen.
import moment from 'moment';
import 'moment/locale/de';

// Unsichtbare bzw. leicht verwechselbare Zeichen nicht im Quelltext tippen
export const MINUS = String.fromCharCode(0x2212);
export const NBSP = String.fromCharCode(0xa0);
export const LEER = String.fromCharCode(0x2013); // „–“ für fehlende Werte

const formatter = {};

function numberFormat(digits) {
  if (!formatter[digits]) {
    formatter[digits] = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  }
  return formatter[digits];
}

function toNumber(x) {
  if (x === null || x === undefined || x === '') {
    return NaN;
  }
  return typeof x === 'number' ? x : Number(x);
}

// „−0,00“ vermeiden: steht nach dem Runden keine Ziffer ungleich 0 da, entfällt das Vorzeichen
function withSign(text) {
  if (!/[1-9]/.test(text)) {
    return text.replace('-', '').replace(MINUS, '');
  }
  return text.replace('-', MINUS);
}

// 1234.5 → „1.235“; formatNumber(1234.5, 1) → „1.234,5“; −3 → „−3“
export function formatNumber(x, digits = 0) {
  const n = toNumber(x);
  if (!isFinite(n)) {
    return LEER;
  }
  return withSign(numberFormat(digits).format(n));
}

// 13091.69 → „13.091,69 €“; −3.2 → „−3,20 €“
export function formatEuro(x) {
  const text = formatNumber(x, 2);
  return text === LEER ? LEER : text + NBSP + '€';
}

// Anteil als Bruch: 0.042 → „+4,2 %“; −0.01 → „−1,0 %“
export function formatPercent(x, digits = 1) {
  const n = toNumber(x);
  if (!isFinite(n)) {
    return LEER;
  }
  const betrag = numberFormat(digits).format(Math.abs(n * 100));
  const vorzeichen = n < 0 && /[1-9]/.test(betrag) ? MINUS : '+';
  return vorzeichen + betrag + NBSP + '%';
}

// Datum/Zeit: Date, moment, ISO-Text („2026-10-08T11:42:00Z“, „2026-10-08 11:42:00“) oder Zeitstempel
function toMoment(t) {
  if (t === null || t === undefined || t === '') {
    return null;
  }
  const m = moment.isMoment(t) ? t.clone() : moment(t);
  return m.isValid() ? m.locale('de') : null;
}

// „08.10.2026, 11:42“
export function formatDateTime(t) {
  const m = toMoment(t);
  return m ? m.format('DD.MM.YYYY, HH:mm') : LEER;
}

// „08.10., 11:42“
export function formatDateTimeShort(t) {
  const m = toMoment(t);
  return m ? m.format('DD.MM., HH:mm') : LEER;
}

// „08.10.2026“
export function formatDate(t) {
  const m = toMoment(t);
  return m ? m.format('DD.MM.YYYY') : LEER;
}

// „12:04 Uhr“
export function formatTime(t) {
  const m = toMoment(t);
  return m ? m.format('HH:mm') + ' Uhr' : LEER;
}

// Monat als 'YYYY-MM' (wie alle Monatsfelder der App), Date oder moment
function toMonth(m) {
  if (typeof m === 'string') {
    const parsed = moment(m, 'YYYY-MM', true);
    return parsed.isValid() ? parsed.locale('de') : toMoment(m);
  }
  return toMoment(m);
}

// '2026-10' → „Oktober 2026“
export function formatMonth(m) {
  const mm = toMonth(m);
  return mm ? mm.format('MMMM YYYY') : LEER;
}

// '2026-10' → „Okt.“; auch Monatszahl 1–12 („Jan.“ … „Dez.“)
export function formatMonthShort(m) {
  if (typeof m === 'number') {
    if (m < 1 || m > 12) {
      return LEER;
    }
    return moment({ year: 2000, month: m - 1, day: 1 }).locale('de').format('MMM');
  }
  const mm = toMonth(m);
  return mm ? mm.format('MMM') : LEER;
}

// Zahlen unverändert; Texte im en-US-Format von MySQL FORMAT() („13,091.69“) → 13091.69; sonst NaN
export function parseApiDecimal(v) {
  if (typeof v === 'number') {
    return v;
  }
  if (typeof v !== 'string') {
    return NaN;
  }
  const s = v.trim().replace(/,/g, '');
  if (!/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(s)) {
    return NaN;
  }
  return Number(s);
}

// plural(1, 'Benutzer', 'Benutzern') → „Benutzer“; nur das Wort, die Zahl setzt der Aufrufer davor
export function plural(n, einzahl, mehrzahl) {
  return Math.abs(Number(n)) === 1 ? einzahl : mehrzahl;
}
